/**
 * MJJ Trading Tool Main Application
 * 整合 Header、InputForm、CardPreview、ActionButtons
 * 實作左右兩欄響應式佈局
 * Requirements: 10.1, 10.2, 10.3
 */

import { useRef } from 'react';
import { Header } from './components/Header';
import {
  BasicInfoSection,
  SpecsSection,
  NetworkSection,
  FinanceSection,
  ContactSection,
} from './components/InputForm';
import { TradingCard } from './components/CardPreview';
import { ActionButtons } from './components/ActionButtons';
import { useCardState } from './hooks/useCardState';
import { useLanguage } from './hooks/useLanguage';

function App() {
  const {
    cardData,
    result,
    handleCategoryChange,
    updateField,
    updateDisks,
    updateTraffic,
    updateContact,
    showSpecs,
  } = useCardState();

  const { language, setLanguage, t } = useLanguage();

  // Ref for card image export
  const cardRef = useRef<HTMLDivElement>(null);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <Header
          category={cardData.category}
          onCategoryChange={handleCategoryChange}
          language={language}
          onLanguageChange={setLanguage}
          t={t}
        />

        {/* Main Content - Two Column Layout */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Column - Input Form */}
          <div className="flex-1 space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <BasicInfoSection
                category={cardData.category}
                provider={cardData.provider}
                productName={cardData.productName}
                region={cardData.region}
                remarks={cardData.remarks}
                onProviderChange={(v) => updateField('provider', v)}
                onProductNameChange={(v) => updateField('productName', v)}
                onRegionChange={(v) => updateField('region', v)}
                onRemarksChange={(v) => updateField('remarks', v)}
                t={t}
              />
            </div>

            {/* Specs Section - Only for server types */}
            {showSpecs && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <SpecsSection
                  category={cardData.category}
                  cpu={cardData.cpu}
                  ram={cardData.ram}
                  disks={cardData.disks}
                  routeType={cardData.routeType}
                  customRoute={cardData.customRoute}
                  onCpuChange={(v) => updateField('cpu', v)}
                  onRamChange={(v) => updateField('ram', v)}
                  onDisksChange={updateDisks}
                  onRouteTypeChange={(v) => updateField('routeType', v)}
                  onCustomRouteChange={(v) => updateField('customRoute', v)}
                  t={t}
                />
              </div>
            )}

            {/* Network Section - Only for server types */}
            {showSpecs && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <NetworkSection
                  traffic={cardData.traffic}
                  onTrafficChange={updateTraffic}
                  t={t}
                />
              </div>
            )}

            <div className="bg-white rounded-lg shadow-md p-6">
              <FinanceSection
                currency={cardData.currency}
                renewalPrice={cardData.renewalPrice}
                sellingPrice={cardData.sellingPrice}
                billingCycle={cardData.billingCycle}
                purchaseDate={cardData.purchaseDate}
                nextDueDate={cardData.nextDueDate}
                exchangeRate={cardData.exchangeRate}
                targetCurrency={cardData.targetCurrency}
                onCurrencyChange={(v) => updateField('currency', v)}
                onRenewalPriceChange={(v) => updateField('renewalPrice', v)}
                onSellingPriceChange={(v) => updateField('sellingPrice', v)}
                onBillingCycleChange={(v) => updateField('billingCycle', v)}
                onPurchaseDateChange={(v) => updateField('purchaseDate', v)}
                onNextDueDateChange={(v) => updateField('nextDueDate', v)}
                onExchangeRateChange={(v) => updateField('exchangeRate', v)}
                onTargetCurrencyChange={(v) => updateField('targetCurrency', v)}
                t={t}
              />
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <ContactSection
                contact={cardData.contact}
                onContactChange={updateContact}
                t={t}
              />
            </div>
          </div>

          {/* Right Column - Card Preview (Sticky) */}
          <div className="lg:w-[520px] lg:flex-shrink-0">
            <div className="lg:sticky lg:top-6 space-y-4">
              <TradingCard
                ref={cardRef}
                cardData={cardData}
                result={result}
                t={t}
              />

              <ActionButtons
                cardData={cardData}
                result={result}
                cardRef={cardRef}
                t={t}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
